// src/pages/dashboards/DashboardsPage.tsx
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Badge, Spinner } from '@/components/ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/Dropdown';
import {
  Plus,
  Star,
  Eye,
  Edit2,
  Copy,
  Trash2,
  MoreHorizontal,
  LayoutDashboard,
  Share2,
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  useDashboards,
  useDeleteDashboard,
  useSetDashboardDefault,
  useDuplicarDashboard,
} from '@/hooks/queries/useReportes';
import { DashboardFormModal } from './components/DashboardFormModal';
import type { DashboardListItem } from '@/types/reporte';

export function DashboardsPage() {
  const navigate = useNavigate();
  const { data: dashboards, isLoading } = useDashboards();
  const deleteMutation = useDeleteDashboard();
  const setDefaultMutation = useSetDashboardDefault();
  const duplicarMutation = useDuplicarDashboard();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDashboard, setEditingDashboard] = useState<DashboardListItem | undefined>();

  const handleView = (id: string) => {
    navigate(`/dashboards/${id}`);
  };

  const handleEdit = (dashboard: DashboardListItem) => {
    setEditingDashboard(dashboard);
    setIsModalOpen(true);
  };

  const handleDuplicate = async (dashboard: DashboardListItem) => {
    const nuevoNombre = `${dashboard.nombre} (copia)`;
    await duplicarMutation.mutateAsync({ id: dashboard.id, nombre: nuevoNombre });
  };

  const handleSetDefault = async (id: string) => {
    await setDefaultMutation.mutateAsync(id);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Estas seguro de que deseas eliminar este dashboard?')) {
      await deleteMutation.mutateAsync(id);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingDashboard(undefined);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Dashboards</h1>
          <p className="text-gray-500">Gestiona tus dashboards y visualizaciones</p>
        </div>
        <Button onClick={() => setIsModalOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Dashboard
        </Button>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : !dashboards || dashboards.length === 0 ? (
        <Card className="p-12">
          <div className="text-center">
            <LayoutDashboard className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No tienes dashboards
            </h3>
            <p className="text-gray-500 mb-4">
              Crea tu primer dashboard para visualizar métricas importantes
            </p>
            <Button onClick={() => setIsModalOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Crear dashboard
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {dashboards.map((dashboard) => (
            <Card
              key={dashboard.id}
              className="p-4 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => handleView(dashboard.id)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-gray-900 truncate">
                      {dashboard.nombre}
                    </h3>
                    {dashboard.es_default && (
                      <Star className="h-4 w-4 text-yellow-500 fill-yellow-500 flex-shrink-0" />
                    )}
                    {dashboard.es_compartido && (
                      <Share2 className="h-4 w-4 text-blue-500 flex-shrink-0" />
                    )}
                  </div>
                  {dashboard.descripcion && (
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                      {dashboard.descripcion}
                    </p>
                  )}
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenuItem onClick={() => handleView(dashboard.id)}>
                      <Eye className="h-4 w-4 mr-2" />
                      Ver
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleEdit(dashboard)}>
                      <Edit2 className="h-4 w-4 mr-2" />
                      Editar
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleDuplicate(dashboard)}>
                      <Copy className="h-4 w-4 mr-2" />
                      Duplicar
                    </DropdownMenuItem>
                    {!dashboard.es_default && (
                      <DropdownMenuItem onClick={() => handleSetDefault(dashboard.id)}>
                        <Star className="h-4 w-4 mr-2" />
                        Establecer como default
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-red-600"
                      onClick={() => handleDelete(dashboard.id)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Eliminar
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <div className="flex items-center justify-between mt-4 pt-3 border-t">
                <Badge>
                  {dashboard.widget_count} widgets
                </Badge>
                <span className="text-xs text-gray-500">
                  Actualizado{' '}
                  {format(new Date(dashboard.updated_at), "d MMM yyyy", { locale: es })}
                </span>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog */}
      <DashboardFormModal
        open={isModalOpen}
        onClose={handleCloseModal}
        dashboard={editingDashboard}
      />
    </div>
  );
}
